import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts'

import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'

import { ChartCard, ChartTooltip, chartAxis, chartGrid, compactNumber } from '@/components/dashboard/chart-kit'

import { STEP_LABELS } from './constants'

import type { AnalyticsOverview, AnalyticsProductRow } from '@/types/analytics'

/** The page's date range plus the device filter, sent with every view that can be split by device. */
export type AnalyticsFilters = { from_date: string; to_date: string; device?: string }

export type QueryState<T> = { data?: T; isLoading: boolean; isError: boolean; error: Error | null }

export const LowSample = ({ show }: { show: boolean }) => (show ? <Badge tone="warning" className="ml-1.5">Few visits</Badge> : null)

/** Share of sessions that reached each step, from visit to confirmed order. */
export const JourneyChart = ({ o }: { o?: AnalyticsOverview }) =>
  o ? (
    <ChartCard
      title="Shopper journey"
      value={`${o.totals.conversion_rate}%`}
      subheader="Share of sessions that reached each step"
      empty={!o.totals.sessions}
      emptyText="No visits in these dates."
      height={260}
      table={{
        columns: ['Step', 'Sessions'],
        rows: o.funnel.map((s) => [STEP_LABELS[s.step], `${s.sessions.toLocaleString()} (${s.rate_from_start}%)`]),
      }}
    >
      <BarChart data={o.funnel.map((s) => ({ ...s, label: STEP_LABELS[s.step] }))} layout="vertical" margin={{ left: 8, right: 16 }}>
        <CartesianGrid {...chartGrid} horizontal={false} vertical />
        <XAxis type="number" {...chartAxis} tickFormatter={compactNumber} allowDecimals={false} />
        <YAxis type="category" dataKey="label" {...chartAxis} width={120} />
        <Tooltip content={<ChartTooltip format={(v) => Number(v).toLocaleString()} />} />
        <Bar dataKey="sessions" name="Sessions" fill="hsl(var(--chart-1))" radius={[0, 4, 4, 0]} />
      </BarChart>
    </ChartCard>
  ) : (
    <Skeleton className="h-96" />
  )

/** Products with their views, add-to-carts and orders; `compact` drops the detail columns for the Overview. */
export const ProductsTable = ({ q, compact = false }: { q: QueryState<AnalyticsProductRow[]>; compact?: boolean }) =>
  q.isLoading ? (
    <Skeleton className="h-48" />
  ) : q.isError ? (
    <p className="text-sm text-error">{q.error?.message}</p>
  ) : !q.data?.length ? (
    <p className="py-6 text-center text-sm text-muted-foreground">No product views in these dates.</p>
  ) : (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Product</TableHead>
          <TableHead className="text-right">Views</TableHead>
          {!compact && <TableHead className="text-right">Added to cart</TableHead>}
          <TableHead className="text-right">Add rate</TableHead>
          <TableHead className="text-right">Ordered</TableHead>
          {!compact && <TableHead className="text-right">Variant picks</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {q.data.map((p) => (
          <TableRow key={p.product_id}>
            <TableCell className="max-w-72 truncate">{p.name ?? 'Deleted product'}</TableCell>
            <TableCell className="text-right tabular-nums">{p.views.toLocaleString()}</TableCell>
            {!compact && <TableCell className="text-right tabular-nums">{p.adds.toLocaleString()}</TableCell>}
            <TableCell className="text-right tabular-nums">
              {p.add_rate}%
              <LowSample show={p.low_sample} />
            </TableCell>
            <TableCell className="text-right tabular-nums">{p.purchases.toLocaleString()}</TableCell>
            {!compact && <TableCell className="text-right tabular-nums">{p.variant_picks.toLocaleString()}</TableCell>}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
